/**
 * useListinoOptions — dynamic dropdown options sourced from MongoDB catalogue.
 *
 * Usage:
 *   const { options, loading, error } = useListinoOptions("F02", "P10_materiale", {
 *     P01_DN: "DN40",
 *     P30_spessore: 2,
 *   });
 *
 * Behavior:
 *   - Key the in-memory cache by `${famiglia}:${pcode}:${stringify(filters)}`
 *   - TTL 5 minutes; stale entries auto-evicted on read.
 *   - Fallback: if fetch fails (5xx/timeout), `options === null` so the
 *     caller can fall back to static PARAM_VALUES.
 *   - Skips the fetch entirely when `pcode` has no listino mapping (e.g.
 *     P22_certificazioni not in PCODE_TO_FIELD) — in that case caller MUST
 *     use static values.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

import { API } from "../lib/backend";
const TTL_MS = 5 * 60 * 1000;
const CACHE = new Map(); // key -> { at, values }

// Backend mapping — mirrors services/listino_filter.py::PCODE_TO_FIELD.
// Params NOT in this set cannot be narrowed via listino → returns null.
const SUPPORTED = new Set([
  "P01_DN",
  "P06_PN",
  "P10_materiale",
  "P11_tenuta",
  "P02_connessione",
  "P13_tipo_otturatore",
  "P16_azionamento",
  "P18_fluido",
  "P22_certificazioni",
  "P30_spessore",
  "P31_schedule",
  "P33_forma_fondello",
  "P34_tipo_flangia",
]);

function cacheKey(famiglia, pcode, filters) {
  const cleaned = Object.fromEntries(
    Object.entries(filters || {}).filter(
      ([, v]) => v !== undefined && v !== null && v !== ""
    )
  );
  return `${famiglia}:${pcode}:${JSON.stringify(cleaned)}`;
}

export default function useListinoOptions(famiglia, pcode, filters) {
  const [state, setState] = useState({
    options: null,
    loading: false,
    error: null,
  });
  const lastKey = useRef(null);

  useEffect(() => {
    if (!famiglia || !pcode) return;
    if (!SUPPORTED.has(pcode)) {
      setState({ options: null, loading: false, error: null });
      return;
    }
    const key = cacheKey(famiglia, pcode, filters);
    if (lastKey.current === key) return;
    lastKey.current = key;

    const cached = CACHE.get(key);
    if (cached && Date.now() - cached.at < TTL_MS) {
      setState({ options: cached.values, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const params = Object.fromEntries(
      Object.entries(filters || {}).filter(
        ([, v]) => v !== undefined && v !== null && v !== ""
      )
    );
    axios
      .get(`${API}/listino/options/${famiglia}/${pcode}`, {
        params,
        withCredentials: true,
        timeout: 5000,
      })
      .then((r) => {
        if (cancelled) return;
        const values = r.data?.values || [];
        CACHE.set(key, { at: Date.now(), values });
        setState({ options: values, loading: false, error: null });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          options: null,
          loading: false,
          error: e?.message || "fetch-error",
        });
      });

    return () => {
      cancelled = true;
    };
    // filters is stringified inside cacheKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [famiglia, pcode, cacheKey(famiglia, pcode, filters)]);

  return state;
}

export function invalidateListinoCache() {
  CACHE.clear();
}
